import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Film, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ForgotPasswordPage() {
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');
  
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  
  const { forgotPassword, resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      await forgotPassword(email);
      setEmailSent(true);
      toast.success('Reset instructions sent to your email');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await resetPassword(resetToken, newPassword);
      setResetComplete(true);
      toast.success('Password reset successfully!');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center glow-primary">
            <Film className="w-7 h-7 text-primary" />
          </div>
          <span className="font-outfit font-bold text-2xl text-white">ClipSync Pro</span>
        </div>

        <Card className="bg-zinc-900/50 border-zinc-800 backdrop-blur">
          {resetToken ? (
            // Reset password form
            <>
              <CardHeader className="text-center pb-4">
                <CardTitle className="font-outfit text-2xl text-white">
                  {resetComplete ? 'Password Reset!' : 'Set new password'}
                </CardTitle>
                <CardDescription className="text-zinc-400">
                  {resetComplete 
                    ? 'Redirecting you to login...' 
                    : 'Enter your new password below'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {resetComplete ? (
                  <div className="flex flex-col items-center py-8">
                    <CheckCircle className="w-16 h-16 text-primary mb-4" />
                    <p className="text-zinc-300">Your password has been reset</p>
                  </div>
                ) : (
                  <form onSubmit={handleResetSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="newPassword" className="text-zinc-300">New Password</Label>
                      <Input
                        id="newPassword"
                        type="password"
                        placeholder="••••••••"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-primary"
                        data-testid="reset-new-password-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-zinc-300">Confirm Password</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-primary"
                        data-testid="reset-confirm-password-input"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 glow-primary"
                      disabled={loading}
                      data-testid="reset-submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Resetting...
                        </>
                      ) : (
                        'Reset password'
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </>
          ) : (
            // Forgot password form
            <>
              <CardHeader className="text-center pb-4">
                <CardTitle className="font-outfit text-2xl text-white">
                  {emailSent ? 'Check your email' : 'Forgot password?'}
                </CardTitle>
                <CardDescription className="text-zinc-400">
                  {emailSent 
                    ? "We've sent password reset instructions to your email" 
                    : "Enter your email and we'll send you reset instructions"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {emailSent ? (
                  <div className="flex flex-col items-center py-4">
                    <CheckCircle className="w-16 h-16 text-primary mb-4" />
                    <p className="text-zinc-300 text-center mb-6">
                      Check your inbox for reset instructions
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => setEmailSent(false)}
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                    >
                      Try another email
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotSubmit} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-zinc-300">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-primary"
                        data-testid="forgot-email-input"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 glow-primary"
                      disabled={loading}
                      data-testid="forgot-submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Send reset instructions'
                      )}
                    </Button>
                  </form>
                )}

                <div className="mt-6 text-center">
                  <Link 
                    to="/login" 
                    className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
                    data-testid="back-to-login-link"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to sign in
                  </Link>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
